import React, { forwardRef, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TextInputFocusEventData,
  TextInputProps,
  View,
  ViewStyle,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

/**
 * Fidelity Purple input field with a left icon and an optional right action.
 * Mirrors the Stitch login form fields (pl-12 offset, rounded-xl, focus ring).
 */
interface IconTextFieldProps extends TextInputProps {
  label?: string;
  leftIcon: keyof typeof MaterialIcons.glyphMap;
  rightIcon?: keyof typeof MaterialIcons.glyphMap;
  onRightIconPress?: () => void;
  error?: string;
  containerStyle?: ViewStyle;
  labelAccessory?: React.ReactNode; // e.g. "Forgot password?" link next to label
}

export const IconTextField = forwardRef<TextInput, IconTextFieldProps>(
  (
    {
      label,
      leftIcon,
      rightIcon,
      onRightIconPress,
      error,
      containerStyle,
      labelAccessory,
      style,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);
    const focusProgress = useRef(new Animated.Value(0)).current;

    const handleFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(true);
      Animated.timing(focusProgress, {
        toValue: 1,
        duration: 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      onFocus?.(e);
    };

    const handleBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(false);
      Animated.timing(focusProgress, {
        toValue: 0,
        duration: 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      onBlur?.(e);
    };

    const borderColor = focusProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [fpColors.outlineVariant, fpColors.primary],
    });

    return (
      <View style={[styles.container, containerStyle]}>
        {(label || labelAccessory) && (
          <View style={styles.labelRow}>
            {label && <Text style={styles.label}>{label}</Text>}
            {labelAccessory}
          </View>
        )}
        <Animated.View
          style={[
            styles.inputWrapper,
            { borderColor: error ? fpColors.error : borderColor },
            focused && styles.inputWrapperFocused,
          ]}
        >
          <View style={styles.leftIconWrap} pointerEvents="none">
            <MaterialIcons
              name={leftIcon}
              size={22}
              color={fpColors.outline}
            />
          </View>
          <TextInput
            ref={ref}
            style={[styles.input, rightIcon && styles.inputWithRight, style]}
            placeholderTextColor={`${fpColors.outline}99`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          {rightIcon && (
            <Pressable
              onPress={onRightIconPress}
              style={styles.rightIconWrap}
              hitSlop={10}
              accessibilityRole="button"
            >
              <MaterialIcons
                name={rightIcon}
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          )}
        </Animated.View>
        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    );
  },
);

IconTextField.displayName = 'IconTextField';

const styles = StyleSheet.create({
  container: {
    marginBottom: 0, // caller controls spacing via form gap
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  inputWrapper: {
    position: 'relative',
    borderWidth: 1,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    minHeight: 52,
    justifyContent: 'center',
  },
  inputWrapperFocused: {
    // soft ring via shadow (web) / elevation (native) — cheap approximation of
    // Tailwind's `focus:ring-2 focus:ring-primary`.
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 2,
  },
  leftIconWrap: {
    position: 'absolute',
    left: 14,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  rightIconWrap: {
    position: 'absolute',
    right: 14,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  input: {
    paddingLeft: 48,
    paddingRight: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: fpColors.onSurface,
    fontFamily: 'Inter_400Regular',
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  inputWithRight: {
    paddingRight: 48,
  },
  errorText: {
    fontSize: 13,
    color: fpColors.error,
    marginTop: 6,
  },
});
